/**
 * The verdict — "What this means", derived from the audit rather than looked up.
 *
 * This replaces a four-band table keyed on the unified score alone, where every
 * domain scoring 50-69 read the identical two sentences and the description
 * named weaknesses ("lacks the structured data, content authority, or
 * optimization") that were never measured. A site with Schema Audit at 92 was
 * told its structured data was lacking.
 *
 * Rules:
 *
 * - DETERMINISTIC. Templates populated from audit data. No model call, no
 *   randomness, no runtime-authored prose. The same audit always produces the
 *   same sentence, which is what makes it defensible and testable.
 * - Only claims what was measured. The weakest category is named only when it
 *   actually has a score; tools are named only when they returned one. A
 *   withheld or failed check is never described as a weakness.
 * - Single source. `buildVerdict` is the ONE producer; ScoreHero renders the
 *   headline/description and ScoreInterpretation renders the impact, both from
 *   the same object. Neither authors copy of its own.
 */

import type { ToolKey, ToolState } from '@/types/radar'
import { summariseCategories, type CategorySummary } from './categories'
import { usableScore } from './tool-scores'
import { TOOLS } from '@/types/radar'

export type VerdictUrgency = 'critical' | 'warning' | 'moderate' | 'good'

export interface Verdict {
  headline: string
  description: string
  impact: string
  urgency: VerdictUrgency
}

export interface VerdictInput {
  /** Unified score. null when every completed check withheld its grade. */
  score: number | null
  tools: Partial<Record<ToolKey, ToolState>>
  /** Checks in an error state on this run. */
  errorCount: number
  /** Checks flagged as warnings on this run. */
  warningCount: number
}

/** A tool that returned a score, for naming the weakest checks. */
interface ScoredTool {
  key: ToolKey
  name: string
  score: number
}

/** Below this a check is treated as failing, matching the dashboard. */
const FAILING = 50

/**
 * Tools that actually put a prompt to an LLM and read the answer back.
 *
 * Only when one of these produced a score may the verdict say anything about
 * how AI answers behave — "the models we queried", "you are being cited",
 * "you surface sometimes". Every one of them is tier 2, and `handleRun` strips
 * tier-2 tools for free-tier audits, so on the free tier NO model is queried
 * and those sentences would be pure invention. That is the same failure as the
 * copy this module replaced, just narrower.
 */
const ANSWER_LAYER: ReadonlySet<ToolKey> = new Set<ToolKey>([
  'citation',
  'answer',
  'promptSov',
  'hallucinationCheck',
  'brandDisambiguation',
])

const TOOL_NAMES: Record<string, string> = Object.fromEntries(
  TOOLS.map(t => [t.key, t.name])
)

/**
 * What a weak category costs, in AI-visibility terms rather than SEO terms.
 * One clause per category, used to close the description.
 */
const CATEGORY_CONSEQUENCE: Record<string, string> = {
  crawlability: 'AI crawlers cannot reliably reach or map your content',
  content:
    'AI can reach the site but cannot extract a confident answer from it',
  citations:
    'AI answers about your category are being sourced from someone else',
  community: 'the third-party discussions AI leans on do not mention you',
  trust:
    'AI is describing your brand from inference rather than from your own pages',
}

function band(score: number): VerdictUrgency {
  if (score < 30) return 'critical'
  if (score < 50) return 'warning'
  if (score < 70) return 'moderate'
  return 'good'
}

/** Scored tools, weakest first. Withheld/failed/idle are excluded outright. */
function scoredToolsAscending(
  tools: Partial<Record<ToolKey, ToolState>>
): ScoredTool[] {
  const out: ScoredTool[] = []
  for (const tool of TOOLS) {
    const score = usableScore(tools[tool.key])
    if (score === null) continue
    out.push({ key: tool.key, name: TOOL_NAMES[tool.key] ?? tool.key, score })
  }
  return out.sort((a, b) => a.score - b.score)
}

/** The lowest-scoring category that actually produced a score. */
function weakestCategory(
  categories: CategorySummary[]
): (CategorySummary & { score: number }) | null {
  const scored = categories.filter(
    (c): c is CategorySummary & { score: number } => c.score !== null
  )
  if (scored.length === 0) return null
  return scored.reduce((worst, c) => (c.score < worst.score ? c : worst))
}

/** "Schema Audit 12" / "Schema Audit 12 and llms.txt 0" */
function nameTools(list: ScoredTool[]): string {
  const parts = list.map(t => `${t.name} ${t.score}`)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0]
  return `${parts[0]} and ${parts[1]}`
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/**
 * Build the verdict for one audit.
 *
 * Every sentence is assembled from measured values. When the audit produced
 * nothing gradeable the verdict says exactly that instead of inferring.
 */
export function buildVerdict(input: VerdictInput): Verdict {
  const { score, tools, errorCount, warningCount } = input

  const scoredTools = scoredToolsAscending(tools)
  const openIssues = errorCount + warningCount

  // Nothing gradeable: a withheld-only run has no weaknesses to describe, and
  // inventing a band from a score that does not exist is the failure this
  // module was written to remove.
  if (score === null || scoredTools.length === 0) {
    return {
      headline: 'This run produced no gradeable result',
      description:
        'Every completed check declined to grade, so there is no score to interpret. A withheld grade means the check could not measure the site, not that the site scored zero.',
      impact:
        'Re-run the audit, or open the tool details to see why each check withheld.',
      urgency: 'moderate',
    }
  }

  const urgency = band(score)
  const categories = summariseCategories(tools)
  const weakest = weakestCategory(categories)
  const failing = scoredTools.filter(t => t.score < FAILING)
  const total = scoredTools.length

  // ── Headline: the band, stated as a measurement ──────────────────────────
  // When no check is failing outright, the headline falls back to naming the
  // weakest layer. The description then must NOT restate it verbatim.
  const headlineNamesWeakest =
    failing.length === 0 && weakest !== null && weakest.score < 70
  const headline =
    failing.length > 0
      ? `${failing.length} of ${total} ${plural(total, 'check', 'checks')} ${plural(failing.length, 'is', 'are')} failing`
      : headlineNamesWeakest
        ? `${weakest!.label} is the weakest layer at ${weakest!.score}`
        : `All ${total} scored ${plural(total, 'check', 'checks')} are passing`

  // ── Description: weakest category, the tools behind it, the consequence ──
  const descriptionParts: string[] = [
    `${score}/100 across ${total} scored ${plural(total, 'check', 'checks')}.`,
  ]

  if (weakest) {
    // Name the weakest tools from INSIDE the weakest category, so the numbers
    // quoted are the ones that produced the label.
    const inWeakest = scoredTools.filter(t =>
      weakest.members.some(m => m.key === t.key)
    )
    const named = nameTools(inWeakest.slice(0, 2))
    if (headlineNamesWeakest) {
      // Headline already carried the label and score; add only the evidence.
      if (named) descriptionParts.push(`Lowest checks: ${named}.`)
    } else {
      descriptionParts.push(
        named
          ? `${weakest.label} is the weakest layer at ${weakest.score} (${named}).`
          : `${weakest.label} is the weakest layer at ${weakest.score}.`
      )
    }
    const consequence = CATEGORY_CONSEQUENCE[weakest.key]
    if (consequence) descriptionParts.push(`That means ${consequence}.`)
  }

  if (openIssues > 0) {
    const bits: string[] = []
    if (errorCount > 0)
      bits.push(
        `${errorCount} critical ${plural(errorCount, 'issue', 'issues')}`
      )
    if (warningCount > 0)
      bits.push(
        `${warningCount} ${plural(warningCount, 'warning', 'warnings')}`
      )
    descriptionParts.push(`${bits.join(' and ')} open.`)
  }

  // ── Impact: the practical consequence, scaled to the band ────────────────
  // Two phrasings per band. The first may describe observed AI answer
  // behaviour because a tool measured it; the second may only describe the
  // readable layer, because on this run nothing queried a model.
  const answerLayerMeasured = scoredTools.some(t => ANSWER_LAYER.has(t.key))

  const impact = answerLayerMeasured
    ? urgency === 'critical'
      ? `At ${score}, the models we queried are unlikely to surface you at all. Competitors are being recommended in your place.`
      : urgency === 'warning'
        ? failing.length > 0
          ? `${failing.length} failing ${plural(failing.length, 'check keeps', 'checks keep')} you out of answers you should be winning.`
          : `At ${score}, you are being cited inconsistently and competitors are capturing the difference.`
        : urgency === 'moderate'
          ? weakest
            ? `You surface sometimes, but ${weakest.label} at ${weakest.score} is capping how often.`
            : `You surface sometimes, but not consistently enough to own your category.`
          : failing.length > 0
            ? `Strong overall, though ${failing.length} failing ${plural(failing.length, 'check is', 'checks are')} still costing you answers.`
            : `Strong position. Hold it and expand into adjacent queries.`
    : urgency === 'critical'
      ? `At ${score}, the layer AI reads before it can answer about you is failing. This run did not query any AI answers.`
      : urgency === 'warning'
        ? failing.length > 0
          ? `${failing.length} failing ${plural(failing.length, 'check blocks', 'checks block')} what AI can read from your site.`
          : `At ${score}, what AI can read from your site is materially incomplete.`
        : urgency === 'moderate'
          ? weakest
            ? `${weakest.label} at ${weakest.score} is the ceiling on what AI can read from you.`
            : `Readable, but not thoroughly enough to compete on.`
          : failing.length > 0
            ? `Strong overall, though ${failing.length} failing ${plural(failing.length, 'check', 'checks')} still ${plural(failing.length, 'limits', 'limit')} what AI can read.`
            : `Strong position across every check we ran.`

  return {
    headline,
    description: descriptionParts.join(' '),
    impact,
    urgency,
  }
}
